// Optimises Cloudinary image URLs on the fly.
// - f_auto      → serves WebP / AVIF when the browser supports it (often 70-90% smaller than PNG)
// - q_auto:good → automatic quality (visually identical, much smaller)
// - w_<N>       → caps delivered width so we don't ship 4000px assets to a 1500px slot
//
// Safe no-op for non-Cloudinary URLs.
// Large local PNG artwork that has a visually identical WebP sibling in /public/images.
const LOCAL_SRCSET = {
  "/images/india-2030-ad-banner.jpg": "/static/img/india-2030-ad-banner-960.webp 960w, /static/img/india-2030-ad-banner.webp 1920w",
  "/images/india-2030-ad-strip.jpg": "/static/img/india-2030-ad-strip-960.webp 960w, /static/img/india-2030-ad-strip.webp 1920w",
  "/images/flagship-ips-banner.png": "/images/flagship-ips-banner-960.webp 960w, /images/flagship-ips-banner-1440.webp 1440w, /images/flagship-ips-banner.webp 1672w",
  "/images/bif-2026-banner.png": "/images/bif-2026-banner-960.webp 960w, /images/bif-2026-banner-1440.webp 1440w, /images/bif-2026-banner.webp 1672w",
};
const LOCAL_WEBP = {
  "/images/india-2030-ad-banner.jpg": "/static/img/india-2030-ad-banner.webp",
  "/images/india-2030-banner.png": "/images/india-2030-banner.webp",
  "/images/india-2030-ad-strip.jpg": "/static/img/india-2030-ad-strip.webp",
  "/images/flagship-ips-banner.png": "/images/flagship-ips-banner.webp",
  "/images/bif-2026-banner.png": "/images/bif-2026-banner.webp",
};

export const optimizeImage = (url, { width = 1500, quality = "auto:good" } = {}) => {
  if (!url || typeof url !== "string") return url;
  if (LOCAL_WEBP[url]) return LOCAL_WEBP[url];
  if (!url.includes("res.cloudinary.com") || !url.includes("/upload/")) return url;
  // Avoid double-transforming an already optimised URL
  if (/\/upload\/[a-z_0-9,:]*f_auto/i.test(url)) return url;
  const transforms = `f_auto,q_${quality},w_${width}`;
  return url.replace("/upload/", `/upload/${transforms}/`);
};

export default optimizeImage;

// Responsive candidates for Cloudinary images: the browser picks the smallest width that fills the slot.
export const cloudinarySrcSet = (url, widths = [400, 800, 1200]) => {
  if (!url || typeof url !== "string" || !url.includes("res.cloudinary.com") || !url.includes("/upload/")) return undefined;
  return widths.map((w) => `${optimizeImage(url, { width: w })} ${w}w`).join(", ");
};

export const localSrcSet = (url) => LOCAL_SRCSET[url];

// Explicit-format candidates for <picture>: this Cloudinary account's f_auto returns JPEG to Chrome for many assets
// (measured 232 KB JPEG vs 150 KB AVIF at w_1280, same q_auto:good), so AVIF/WebP are requested by type.
export const cloudinaryTypedSrcSet = (url, format, widths) => {
  if (!url || typeof url !== "string" || !url.includes("res.cloudinary.com") || !url.includes("/upload/")) return undefined;
  if (/\/upload\/[a-z_0-9,:]*f_auto/i.test(url)) return undefined;
  return widths.map((w) => `${url.replace("/upload/", `/upload/f_${format},q_auto:good,w_${w}/`)} ${w}w`).join(", ");
};

// Intrinsic sizes of local banner assets so <img> can reserve space when the CMS record has none.
const LOCAL_DIMS = {
  "/images/india-2030-ad-strip.jpg": [1920, 480],
  "/images/india-2030-ad-banner.jpg": [1920, 480],
  "/images/india-2030-banner.png": [1717, 916],
  "/images/flagship-ips-banner.png": [1672, 941],
  "/images/bif-2026-banner.png": [1672, 941],
};
export const localDims = (url) => LOCAL_DIMS[url];
